import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { Action, ActionStep } from '../../types/action';
import { FiCheck, FiX, FiAlertCircle, FiMail, FiFileText, FiSettings } from 'react-icons/fi';
import { UserRole } from './ActionsPanel';
import {
  type ExecutionType,
  EXECUTION_TYPES,
  EXECUTION_TYPE_LABELS,
  NOTES_MAX_LENGTH,
  detectPII,
  trackRecommendationExecution,
} from '../../services/recommendationExecutionService';

interface ActionExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: Action;
  onComplete: () => void;
  role?: UserRole; // Make role optional to avoid breaking existing usages
}

const ActionExecutionModal: FC<ActionExecutionModalProps> = ({
  isOpen,
  onClose,
  action,
  onComplete,
  role = 'CEO', // Default to CEO if not provided
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [stepStatuses, setStepStatuses] = useState<Record<string, 'pending' | 'in_progress' | 'completed' | 'failed'>>({});
  const [isCompleted, setIsCompleted] = useState(false);

  // P4B: Execution logging state (behavioral recommendations only)
  const isBehavioral = !!action.segmentKey;
  const [showExecutionForm, setShowExecutionForm] = useState(false);
  const [executionType, setExecutionType] = useState<ExecutionType | ''>('');
  const [executionNotes, setExecutionNotes] = useState('');
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get role-specific colors
  const getRoleColors = () => {
    switch (role) {
      case 'CEO':
        return {
          primary: 'bg-brand-purple hover:bg-brand-purple/80',
          spinner: 'border-brand-purple/50 border-t-brand-purple',
          accent: 'text-brand-purple'
        };
      case 'VP of Lending':
        return {
          primary: 'bg-brand-link hover:bg-brand-link/80', 
          spinner: 'border-brand-link/50 border-t-brand-link',
          accent: 'text-brand-link'
        };
      case 'Loan Advisor':
        return {
          primary: 'bg-brand-blue hover:bg-brand-blue/80',
          spinner: 'border-brand-blue/50 border-t-brand-blue',
          accent: 'text-brand-blue'
        };
      default:
        return {
          primary: 'bg-brand-purple hover:bg-brand-purple/80',
          spinner: 'border-brand-purple/50 border-t-brand-purple',
          accent: 'text-brand-purple'
        };
    }
  };
  
  const roleColors = getRoleColors();

  if (!isOpen) return null;

  const handleExecute = async () => {
    setIsExecuting(true);
    
    // Simulate executing steps with delays
    for (const step of action.steps) {
      // Update step status to in_progress
      setStepStatuses(prev => ({
        ...prev,
        [step.id]: 'in_progress'
      }));
      
      // Simulate API call with delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update step status to completed
      setStepStatuses(prev => ({
        ...prev,
        [step.id]: 'completed'
      }));
    }
    
    // Mark action as completed
    setIsExecuting(false);
    setIsCompleted(true);
  };

  const getStepIcon = (step: ActionStep) => {
    switch (step.type) {
      case 'notification':
        return <FiAlertCircle size={20} />;
      case 'email':
        return <FiMail size={20} />;
      case 'loan_review':
        return <FiFileText size={20} />;
      case 'account_update':
        return <FiSettings size={20} />;
      default:
        return <FiCheck size={20} />;
    }
  };

  const getStepStatusIcon = (stepId: string) => {
    const status = stepStatuses[stepId];
    
    if (!status || status === 'pending') {
      return <div className="w-5 h-5 rounded-full border-2 border-slate-300"></div>;
    }
    
    if (status === 'in_progress') {
      return <div className={`w-5 h-5 rounded-full border-2 ${roleColors.spinner} animate-spin`}></div>;
    }
    
    if (status === 'completed') {
      return <FiCheck size={20} className="text-green-600" />;
    }
    
    return <FiX size={20} className="text-red-600" />;
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative rounded-xl max-w-2xl w-full transform transition-all"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div 
            className="px-6 py-4 rounded-t-xl"
            style={{
              background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
              borderBottom: '1px solid rgba(203, 213, 225, 0.5)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-slate-800">{action.title}</h2>
                <p className="text-slate-600 mt-1 text-sm">{action.description}</p>
              </div>
              <button 
                className="text-slate-400 hover:text-slate-600 transition-colors rounded-full p-1 hover:bg-slate-100"
                onClick={onClose}
                disabled={isExecuting}
              >
                <span className="sr-only">Close</span>
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="px-6 py-5">
            {/* Impact Information */}
            <div className="mb-6 bg-slate-50/80 p-4 rounded-lg border border-slate-200/50">
              <h3 className="text-slate-700 text-sm font-medium mb-2">Impact</h3>
              <div className="flex justify-between items-center">
                <p className="text-slate-600 text-sm">{action.impact.description}</p>
                <div className={`text-xl font-bold ${roleColors.accent}`}>
                  {action.impact.type === 'financial' ? '$' : ''}
                  {action.impact.value.toLocaleString()}
                  {action.impact.type === 'percentage' ? '%' : ''}
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="mb-4">
              <h3 className="text-slate-700 text-sm font-medium mb-4">Steps</h3>
              <div className="space-y-3">
                {action.steps.map((step, index) => (
                  <div 
                    key={step.id} 
                    className={`
                      flex items-start p-4 rounded-lg border
                      ${stepStatuses[step.id] === 'completed' ? 'bg-green-50/80 border-green-200' : 'bg-white/60 border-slate-200/50'}
                      ${stepStatuses[step.id] === 'in_progress' 
                        ? `${role === 'CEO' 
                            ? 'bg-purple-50/80 border-purple-200' 
                            : role === 'VP of Lending' 
                              ? 'bg-blue-50/80 border-blue-200' 
                              : 'bg-indigo-50/80 border-indigo-200'}`
                        : ''}
                    `}
                  >
                    <div className="mr-4 mt-1">
                      {getStepStatusIcon(step.id)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="mr-2 text-slate-600">
                          {getStepIcon(step)}
                        </div>
                        <h4 className="text-slate-800 font-medium text-sm">{step.title}</h4>
                      </div>
                      <p className="text-slate-600 text-sm mt-1">{step.description}</p>
                    </div>
                    <div className="text-xs text-slate-500">
                      Step {index + 1}/{action.steps.length}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* P4B: Execution Logging Form (behavioral recommendations only) */}
          {showExecutionForm && isBehavioral && (
            <div className="px-6 py-5 border-t border-slate-200/50">
              <h3 className="text-slate-700 text-sm font-medium mb-3">Log action taken</h3>

              {/* Execution type dropdown */}
              <label className="block text-xs text-slate-500 mb-1" htmlFor="exec-type-select">
                What did you do?
              </label>
              <select
                id="exec-type-select"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-purple/40 mb-3"
                value={executionType}
                onChange={e => { setExecutionType(e.target.value as ExecutionType); setExecutionError(null); }}
              >
                <option value="" disabled>Select action type…</option>
                {EXECUTION_TYPES.map(t => (
                  <option key={t} value={t}>{EXECUTION_TYPE_LABELS[t]}</option>
                ))}
              </select>

              {/* Notes textarea */}
              <label className="block text-xs text-slate-500 mb-1" htmlFor="exec-notes">
                Notes <span className="text-slate-400">(optional)</span>
              </label>
              <textarea
                id="exec-notes"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-brand-purple/40"
                rows={3}
                maxLength={NOTES_MAX_LENGTH}
                placeholder="Describe cohort-level actions taken…"
                value={executionNotes}
                onChange={e => {
                  setExecutionNotes(e.target.value);
                  setExecutionError(null);
                }}
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1 mb-2">
                <span>{executionNotes.length}/{NOTES_MAX_LENGTH}</span>
              </div>

              {/* Privacy warning */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                <FiAlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-[11px] text-amber-700">
                  Do not include member names, SSNs, emails, phone numbers, or other personal information.
                </span>
              </div>

              {/* Inline error */}
              {executionError && (
                <div className="text-xs text-red-600 mb-2">{executionError}</div>
              )}
            </div>
          )}

          {/* Modal Footer */}
          <div
            className="px-6 py-4 rounded-b-xl"
            style={{
              background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
              borderTop: '1px solid rgba(203, 213, 225, 0.5)'
            }}
          >
            <div className="flex justify-end items-center space-x-4">
              {/* --- Pre-completion states --- */}
              {!showExecutionForm && (
                <>
                  <button
                    className="text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                    onClick={onClose}
                    disabled={isExecuting}
                  >
                    Cancel
                  </button>

                  {isCompleted ? (
                    <button
                      className="px-4 py-2 text-sm font-medium text-white rounded transition-all flex items-center"
                      style={{
                        background: 'linear-gradient(to bottom, #10b981, #059669)',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                      }}
                      onClick={() => {
                        if (isBehavioral) {
                          // Show execution logging form instead of closing
                          setShowExecutionForm(true);
                        } else {
                          onComplete();
                          onClose();
                        }
                      }}
                    >
                      Complete <FiCheck className="ml-2" />
                    </button>
                  ) : (
                    <button
                      className="px-4 py-2 text-sm font-medium text-white rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      style={{
                        background: 'linear-gradient(to bottom, #6b7280, #4b5563)',
                        boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)'
                      }}
                      onClick={handleExecute}
                      disabled={isExecuting}
                    >
                      {isExecuting ? 'Processing...' : 'Execute'}
                      {isExecuting && <div className={`ml-2 w-4 h-4 rounded-full border-2 ${roleColors.spinner} animate-spin`}></div>}
                    </button>
                  )}
                </>
              )}

              {/* --- Execution logging buttons (behavioral only) --- */}
              {showExecutionForm && (
                <>
                  <button
                    className="text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                    onClick={() => {
                      // Skip — complete without logging execution
                      onComplete();
                      onClose();
                    }}
                    disabled={isSubmitting}
                  >
                    Skip
                  </button>

                  <button
                    className="px-4 py-2 text-sm font-medium text-white rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    style={{
                      background: 'linear-gradient(to bottom, #10b981, #059669)',
                      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                    }}
                    disabled={isSubmitting}
                    onClick={async () => {
                      // Validate execution type selected
                      if (!executionType) {
                        setExecutionError('Please select an action type.');
                        return;
                      }
                      // Validate notes length
                      if (executionNotes.length > NOTES_MAX_LENGTH) {
                        setExecutionError(`Notes must be ${NOTES_MAX_LENGTH} characters or fewer.`);
                        return;
                      }
                      // Client-side PII check
                      if (executionNotes) {
                        const piiMatch = detectPII(executionNotes);
                        if (piiMatch) {
                          setExecutionError(`Notes appear to contain ${piiMatch}. Please remove personal information.`);
                          return;
                        }
                      }

                      setIsSubmitting(true);
                      // Fire-and-forget — don't block close on failure
                      trackRecommendationExecution({
                        recommendation_key: action.id,
                        segment_key: action.segmentKey!,
                        execution_type: executionType as ExecutionType,
                        execution_notes: executionNotes || undefined,
                      });

                      onComplete();
                      onClose();
                    }}
                  >
                    {isSubmitting ? 'Saving…' : 'Log & Close'} <FiCheck className="ml-2" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ActionExecutionModal;